import { useEffect, useRef, useState } from 'react';

import { szam, szamotOlvas } from '../formazas.ts';

/**
 * Terv/tény cella az ellenőrzéshez.
 *
 * A cella tetején halványszürkén a reggel kiadott **terv**, alatta a beírható
 * **tény**. Ha a kettő eltér, a mező sárgára vált — nem hiba, csak „nézd meg".
 *
 * Az Enter a következő sorra ugrik **ugyanabban az oszlopban**: az irodán
 * oszloponként haladnak végig a lapon, nem soronként.
 */
export function TenyMezo({
  terv,
  ertek,
  onMent,
  tiltva,
  oszlop,
  sorIndex,
  helykitolto = '0',
  /** Az „x" jelölés a terven: telefonon egyeztetett mennyiség. */
  tervX = false,
  osztaly,
  cim,
}: {
  terv: number | null;
  ertek: number | null;
  onMent: (uj: string) => Promise<void>;
  tiltva?: boolean;
  oszlop: string;
  sorIndex: number;
  helykitolto?: string;
  tervX?: boolean;
  /** Kiegészítő stílusosztály (pl. az F+ mennyiség, a számított kanna). */
  osztaly?: string;
  /** Magyarázó szöveg az egér alá. */
  cim?: string;
}) {
  const eredeti = ertek === null || ertek === undefined ? '' : String(ertek);
  const [szoveg, setSzoveg] = useState(eredeti);
  const [hiba, setHiba] = useState<string | null>(null);
  const [ment, setMent] = useState(false);
  const utolso = useRef(eredeti);

  useEffect(() => {
    if (eredeti !== utolso.current) {
      utolso.current = eredeti;
      setSzoveg(eredeti);
    }
  }, [eredeti]);

  async function mentes() {
    const uj = szoveg.trim();
    if (uj === utolso.current.trim()) return;
    if (uj && szamotOlvas(uj) === null) {
      setHiba('Ez nem szám.');
      setSzoveg(utolso.current);
      return;
    }
    setMent(true);
    setHiba(null);
    try {
      await onMent(uj);
      utolso.current = uj;
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A mentés nem sikerült.');
      setSzoveg(utolso.current);
    } finally {
      setMent(false);
    }
  }

  const tervSzam = terv ?? null;
  const tenySzam = szamotOlvas(szoveg);
  // Eltérés akkor van, ha mindkettő ki van töltve és nem egyezik.
  const elter =
    tervSzam !== null && tenySzam !== null && Math.abs(tervSzam - tenySzam) > 0.001;

  const tervFelirat = tervX ? 'x' : tervSzam ? szam(tervSzam, 0) : null;

  return (
    <>
      {tervFelirat && <span className="terv">{tervFelirat}</span>}
      <input
        className={`c${elter ? ' diff' : ''}${osztaly ? ` ${osztaly}` : ''}`}
        value={szoveg}
        placeholder={helykitolto}
        disabled={tiltva || ment}
        title={hiba ?? (elter ? `A tervezett mennyiség ${tervFelirat} volt.` : cim)}
        data-oszlop={oszlop}
        data-sorindex={sorIndex}
        style={hiba ? { background: 'var(--badw)' } : undefined}
        onChange={(e) => {
          setSzoveg(e.target.value);
          if (hiba) setHiba(null);
        }}
        onBlur={() => void mentes()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            // Előbb mentünk, aztán ugrunk — hogy a következő sor már
            // a frissített összesítést lássa.
            void mentes().then(() => kovetkezoSor(oszlop, sorIndex));
          }
          if (e.key === 'Escape') {
            setSzoveg(utolso.current);
            setHiba(null);
            e.currentTarget.blur();
          }
        }}
      />
    </>
  );
}

/** Ugrás a következő sorra ugyanabban az oszlopban. */
function kovetkezoSor(oszlop: string, sorIndex: number): void {
  const kovetkezo = document.querySelector<HTMLInputElement>(
    `input[data-oszlop="${oszlop}"][data-sorindex="${sorIndex + 1}"]`,
  );
  if (kovetkezo && !kovetkezo.disabled) {
    kovetkezo.focus();
    kovetkezo.select();
  }
}
